/**
 * Phase2CapacityLegend.tsx
 * Legend for the "Team Allocation & Weekly Capacity" grid. Extracted from
 * Phase2CapacityTable.
 *
 * Tile restyle (2026-07-13, Gigi feedback): the confusing per-day "N tests"
 * count cells are gone, so the old "CELL = DAY STATE / Busy (number = tests) /
 * Heavily stacked (>3 tests)" keys are removed. What remains mirrors the
 * Employee Schedule legend (EmployeeScheduleLegend):
 *   • WEEK TILE = utilization band (Under / Healthy / At / Over).
 *   • WORK colours (task + each absence kind) — the bars in the day breakdown
 *     you get by clicking a tile.
 *   • The day-off / conflict keys (a plain day off; a task assigned on a day off
 *     — the one hard conflict).
 * Bar colours come from the same resolver the breakdown bars use
 * (employeeBarColor) so the swatches can't drift.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { employeeBarColor } from '../../../../components/schedule/employeeScheduleShared';
import type { EmployeeBarKind } from '../../../../services/scheduleEmployeeBars';

const labelSx = { fontSize: '0.68rem', color: 'text.secondary' } as const;
const groupLabelSx = {
  fontSize: '0.58rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'text.disabled',
  mr: 0.25,
} as const;

const WORK_KINDS: { kind: EmployeeBarKind; label: string }[] = [
  { kind: 'Task', label: 'Task / work order' },
  { kind: 'Vacation', label: 'Vacation' },
  { kind: 'Sick', label: 'Sick' },
  { kind: 'FieldWork', label: 'Field work' },
  { kind: 'Other', label: 'Other' },
];

/** A solid-color square swatch + label. */
const Swatch: React.FC<{ color: string; label: string; ring?: string }> = ({ color, label, ring }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 12, height: 12, borderRadius: '2px', flexShrink: 0,
          backgroundColor: color,
          border: `1px solid ${theme.border.color}`,
          ...(ring ? { boxShadow: `inset 0 0 0 2px ${ring}` } : {}),
        }}
      />
      <Typography variant="caption" sx={labelSx}>{label}</Typography>
    </Box>
  );
};

/** A thin vertical rule separating the legend groups. */
const GroupDivider: React.FC = () => {
  const theme = useTheme();
  return <Box aria-hidden="true" sx={{ width: '1px', alignSelf: 'stretch', minHeight: 14, backgroundColor: theme.border.color, mx: 0.5 }} />;
};

export const Phase2CapacityLegend: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center', mt: 0.5, px: 0.5, rowGap: 0.5 }}>
      {/* ── Group 1 — WEEK TILE utilization band (Staffing Allocation %, D5) ──── */}
      <Typography sx={groupLabelSx}>Tile&nbsp;=&nbsp;utilization</Typography>
      <Swatch color={theme.schedule.allocUnder} label="Under (<50%)" />
      <Swatch color={theme.schedule.allocHealthy} label="Healthy (50–85%)" />
      <Swatch color={theme.schedule.allocAt} label="At capacity (86–100%)" />
      <Swatch color={theme.schedule.allocOver} label="Over (>100%)" />

      <GroupDivider />

      {/* ── Group 2 — WORK colours (the bars in the click-to-expand breakdown) ── */}
      <Typography sx={groupLabelSx}>Work</Typography>
      {WORK_KINDS.map(({ kind, label }) => (
        <Swatch key={kind} color={employeeBarColor(theme, kind)} label={label} />
      ))}

      <GroupDivider />

      {/* ── Group 3 — day-off / conflict signals (now shown ON the tile) ──────── */}
      <Typography sx={groupLabelSx}>Signals</Typography>
      <Swatch color={alpha(theme.schedule.noActivity, 0.45)} label="Day off (vacation / sick / field work)" />
      <Swatch
        color={alpha(theme.schedule.heatmapHigh, 0.12)}
        ring={theme.schedule.heatmapHigh}
        label="Conflict — assigned on a day off"
      />
    </Box>
  );
};
